import { create } from "@bufbuild/protobuf";
import { FieldMaskSchema, type FieldMask } from "@bufbuild/protobuf/wkt";
import type { PaginationArguments, PaginationPageInfo } from "#gen/common/v1/pagination_pb.js";
import { ShortUrlSchema, ShortUrlsService, type AddShortUrlToBatchRequest, type BulkImportShortUrlsRequest, type ShortUrl } from "#gen/shorturls/v1/shorturls_pb.js";
import { createClient, type ClientOptions } from "#lib/api/api.js";
import { AssetState } from "#lib/api/bundles.js";
import { handleError } from "#lib/api/error.js";
import type { Filter } from "#lib/api/pagination.js";
import { PUBLIC_URLS } from "#lib/url.js";
import type { CallOptions, Client } from "@connectrpc/connect";

export interface ShortUrlList {
	shortUrls: ShortUrl[];
	pageInfo?: PaginationPageInfo;
}

export class ShortUrlsClient {
	private client: Client<typeof ShortUrlsService>;

	constructor(options?: () => ClientOptions) {
		this.client = createClient(PUBLIC_URLS.shortUrls || "/", ShortUrlsService, options);
	}

	async listShortUrlsInBatch(orgId: string, workspaceId: string, batchId: string, batchVersion?: string, filter?: Filter, omitDefaultMetadata?: boolean, pagination?: PaginationArguments, options?: CallOptions): Promise<ShortUrlList> {
		try {
			const res = await this.client.listShortUrls(
				{
					orgId,
					workspaceId,
					batchId,
					namePrefix: filter?.namePrefix,
					tags: filter?.tags,
					folderPath: filter?.folderPath,
					pagination,
					exactName: filter?.exactName,
					states: filter?.states,
					batchVersion,
					omitDefaultMetadata,
				},
				options,
			);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getShortUrl(id: string, omitDefaultMetadata?: boolean): Promise<ShortUrl> {
		try {
			const res = await this.client.getShortUrl({ id, omitDefaultMetadata });
			return res.shortUrl!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async addShortUrlToBatch(req: AddShortUrlToBatchRequest): Promise<ShortUrl> {
		try {
			const res = await this.client.addShortUrlToBatch(req);
			return res.shortUrl!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async bulkImportShortUrls(req: BulkImportShortUrlsRequest): Promise<void> {
		try {
			await this.client.bulkImportShortUrls(req);
		} catch (e) {
			throw handleError(e);
		}
	}

	async archiveShortUrl(id: string): Promise<ShortUrl> {
		try {
			const res = await this.client.updateShortUrl({
				shortUrl: create(ShortUrlSchema, {
					id: id,
					state: AssetState.ARCHIVED,
				}),
				mask: create(FieldMaskSchema, {
					paths: ["state"],
				}),
			});
			return res.shortUrl!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async restoreShortUrl(id: string): Promise<ShortUrl> {
		try {
			const res = await this.client.updateShortUrl({
				shortUrl: create(ShortUrlSchema, {
					id: id,
					state: AssetState.ACTIVE,
				}),
				mask: create(FieldMaskSchema, {
					paths: ["state"],
				}),
			});
			return res.shortUrl!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getShortUrls(ids: string[]): Promise<ShortUrl[]> {
		try {
			const res = await this.client.getShortUrls({ ids });
			return res.shortUrls!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async updateShortUrl(shortUrl: ShortUrl, mask?: FieldMask, batchVersion?: string): Promise<ShortUrl> {
		try {
			const res = await this.client.updateShortUrl({ shortUrl, mask, batchVersion });
			return res.shortUrl!;
		} catch (e) {
			throw handleError(e);
		}
	}
}
